import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Award, BookOpen, Users, GraduationCap, ArrowRight, Languages, Star, Target, Sparkles, CheckCircle, Headphones, UserCheck, TrendingUp, Heart, Rocket } from 'lucide-react';

const TESTIMONIALS = [
  { text: 'Väldigt pedagogisk lärare, som har tålamod och gör lektionerna motiverande.', author: 'Miriam', role: 'Elev' },
  { text: 'Jag tycker att läraren har ett pedagogiskt sätt att undervisa. Han förklarar tajweed tydligt.', author: 'Sugule', role: 'Förälder' },
  { text: 'Ustadh Ismail was very patient and helpful with Tajweed and proper pronunciation.', author: 'Tasneem & Mohammed Amkor', role: '' },
  { text: 'Jag tycker att lektionerna med Ustadh Ismail är väldigt givande, han är mån om att du ska läsa med korrekt tajweed och han fick mig att inte kunna läsa från mushaf till att kunna göra det på fåtal lektioner med tydlig struktur och planering.', author: 'Abdifatah', role: 'Elev' },
  { text: 'Tycker, lektionen är lärorikt. Lärare går igenom så ofta det som behövs, förklarar tydligt samt uppmuntrar att kämpa vidare och absolut inte ger upp. Väldigt flexibel lärare som ställer upp jämt för sina elever.', author: 'Subhija', role: '' },
];

const AboutPage = () => {
  const { t, language } = useLanguage();
  const sv = language === 'sv';

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="about-page">

      {/* Hero */}
      <section className="py-20 islamic-pattern hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-6 tracking-wider"
            style={{ backgroundColor: 'rgba(11,61,46,0.08)', color: '#0B3D2E', border: '1px solid rgba(11,61,46,0.15)' }}>
            <UserCheck size={14} />
            {sv ? 'OM LÄRAREN' : 'ABOUT THE TEACHER'}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'Dedikerad koranundervisning' : 'Dedicated Quran teaching'}
            <br />
            <span className="text-gold">{sv ? 'med passion' : 'with passion'}</span>
          </h1>
        </div>
      </section>

      {/* Teacher Section */}
      <section className="py-20" data-testid="teacher-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-gold/20 to-transparent rounded-lg blur-xl"></div>
              <div className="relative rounded-lg p-1" style={{ background: 'linear-gradient(to bottom right, var(--bg-tertiary), var(--bg-primary))' }}>
                <div className="rounded-lg overflow-hidden aspect-[4/5]" style={{ backgroundColor: 'var(--bg-primary)' }}>
                  <img
                    src="https://customer-assets.emergentagent.com/job_quran-school-3/artifacts/bc278pvs_WhatsApp%20Image%202026-03-05%20at%2019.48.05.jpeg"
                    alt="Ustadh Ismail"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-gold text-black px-6 py-3 rounded">
                <span className="text-2xl font-bold">5+</span>
                <span className="text-sm block">{sv ? 'års erfarenhet' : 'years experience'}</span>
              </div>
            </div>

            {/* Content */}
            <div>
              <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                Ustadh Ismail
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--gold)' }}>
                {sv ? '5+ års erfarenhet av koranundervisning' : '5+ years of Quran teaching experience'}
              </p>
              <p className="leading-relaxed mb-5" style={{ color: 'var(--text-secondary)', lineHeight: 1.9 }}>
                {sv
                  ? 'Med flera års erfarenhet av att undervisa Koranen erbjuder jag en strukturerad, tydlig och disciplinerad väg till korrekt koranläsning.'
                  : 'With several years of experience teaching the Quran, I offer a structured, clear and disciplined path to correct Quran reading.'}
              </p>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                {sv ? 'Min målsättning är att hjälpa varje elev att:' : 'My goal is to help every student:'}
              </p>
              <div className="space-y-2 mb-6">
                {(sv
                  ? ['Läsa Koranen korrekt', 'Förstå och tillämpa tajweed', 'Utveckla en vacker och trygg recitation']
                  : ['Read the Quran correctly', 'Understand and apply tajweed', 'Develop a beautiful and confident recitation']
                ).map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Sparkles size={16} className="text-gold flex-shrink-0 mt-0.5" />
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                {sv
                  ? 'Jag undervisar elever från olika bakgrunder och nivåer, och anpassar undervisningen efter varje elevs behov. Genom att tala:'
                  : 'I teach students from different backgrounds and levels, and adapt the teaching to each student\'s needs. By speaking:'}
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {['Svenska', 'Engelska', 'Arabiska', 'Somaliska'].map((lang, i) => (
                  <div key={i} className="flex items-center gap-2 p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                    <Languages size={14} className="text-gold" />
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{lang}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm italic" style={{ color: 'var(--text-muted)' }}>
                {sv
                  ? 'kan jag skapa en tydlig och trygg inlärningsmiljö för alla elever.'
                  : 'I can create a clear and safe learning environment for all students.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Teaching method + Qualifications */}
      <section className="py-16" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Method */}
            <div className="p-6 sm:p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3 mb-5">
                <Target size={22} className="text-gold" />
                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? 'Min undervisningsmetod' : 'My teaching method'}
                </h3>
              </div>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                {sv ? 'Varje elev får:' : 'Each student gets:'}
              </p>
              <div className="space-y-3">
                {(sv
                  ? ['En personlig studieplan', 'Tydlig struktur och progression', 'Kontinuerlig uppföljning', 'Direkt korrigering av tajweed']
                  : ['A personal study plan', 'Clear structure and progression', 'Continuous follow-up', 'Direct tajweed correction']
                ).map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                    <CheckCircle size={16} className="text-gold flex-shrink-0" />
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Qualifications */}
            <div className="p-6 sm:p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3 mb-5">
                <Award size={22} className="text-gold" />
                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? 'Kvalifikationer' : 'Qualifications'}
                </h3>
              </div>
              <div className="space-y-3 mb-6">
                {(sv
                  ? ['Certifierad koranlärare', '5+ års undervisningserfarenhet', 'Specialiserad inom tajweed', 'Erfarenhet av både barn och vuxna']
                  : ['Certified Quran teacher', '5+ years of teaching experience', 'Specialized in tajweed', 'Experience with both children and adults']
                ).map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                    <CheckCircle size={16} style={{ color: '#0B3D2E' }} className="flex-shrink-0" />
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm italic" style={{ color: 'var(--text-muted)', lineHeight: 1.8 }}>
                {sv
                  ? 'Jag tror att alla kan lära sig läsa Koranen korrekt \u2014 med rätt vägledning, tålamod och struktur.'
                  : 'I believe everyone can learn to read the Quran correctly \u2014 with the right guidance, patience and structure.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Varför välja mig */}
      <section className="py-20" data-testid="why-choose-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
              <Star size={24} className="inline-block text-gold mr-2 mb-1" />
              {sv ? 'Varför välja mig?' : 'Why choose me?'}
            </h2>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {sv ? 'Att lära sig Koranen är en resa \u2014 och rätt lärare gör hela skillnaden.' : 'Learning the Quran is a journey \u2014 and the right teacher makes all the difference.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* 1. Strukturerad metod */}
            <div className="p-6 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(206,150,8,0.1)', border: '1px solid rgba(206,150,8,0.15)' }}>
                  <Target size={20} className="text-gold" />
                </div>
                <h4 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? '1. Strukturerad och tydlig metod' : '1. Structured and clear method'}
                </h4>
              </div>
              <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {sv ? 'Jag undervisar med en egenutvecklad metod \u2014 Quran Online Method \u2014 där du går steg för steg från nybörjare till flytande läsning.'
                     : 'I teach with a self-developed method \u2014 Quran Online Method \u2014 where you go step by step from beginner to fluent reading.'}
              </p>
              <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>{sv ? 'Du vet alltid:' : 'You always know:'}</p>
              <div className="space-y-1">
                {(sv ? ['Vad du lär dig', 'Var du är i processen', 'Vad nästa steg är'] : ['What you are learning', 'Where you are in the process', 'What the next step is']).map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle size={12} className="text-gold flex-shrink-0" />
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 2. Fokus på uttal */}
            <div className="p-6 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(206,150,8,0.1)', border: '1px solid rgba(206,150,8,0.15)' }}>
                  <Headphones size={20} className="text-gold" />
                </div>
                <h4 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? '2. Fokus på korrekt uttal' : '2. Focus on correct pronunciation'}
                </h4>
              </div>
              <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {sv ? 'Tajweed handlar inte bara om regler \u2014 det handlar om att höra, förstå och tillämpa.'
                     : 'Tajweed is not just about rules \u2014 it\'s about hearing, understanding and applying.'}
              </p>
              <div className="space-y-1">
                {(sv ? ['Direkt korrigering', 'Praktisk träning', 'Tydlig vägledning'] : ['Direct correction', 'Practical training', 'Clear guidance']).map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle size={12} className="text-gold flex-shrink-0" />
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs mt-3 font-semibold" style={{ color: 'var(--text-primary)' }}>
                {sv ? 'Så att du läser korrekt \u2014 inte bara "nästan rätt"' : 'So you read correctly \u2014 not just "almost right"'}
              </p>
            </div>

            {/* 3. Personlig undervisning */}
            <div className="p-6 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(206,150,8,0.1)', border: '1px solid rgba(206,150,8,0.15)' }}>
                  <UserCheck size={20} className="text-gold" />
                </div>
                <h4 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? '3. Personlig undervisning' : '3. Personal teaching'}
                </h4>
              </div>
              <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {sv ? 'Ingen elev är den andra lik. Därför får du:' : 'No student is the same. Therefore you get:'}
              </p>
              <div className="space-y-1">
                {(sv ? ['Anpassad studieplan', 'Undervisning i din takt', 'Full uppmärksamhet'] : ['Adapted study plan', 'Teaching at your pace', 'Full attention']).map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle size={12} className="text-gold flex-shrink-0" />
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 4. Undervisning på ditt språk */}
            <div className="p-6 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(206,150,8,0.1)', border: '1px solid rgba(206,150,8,0.15)' }}>
                  <Languages size={20} className="text-gold" />
                </div>
                <h4 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? '4. Undervisning på ditt språk' : '4. Teaching in your language'}
                </h4>
              </div>
              <div className="space-y-1 mb-3">
                {['Svenska', 'Engelska', 'Arabiska', 'Somaliska'].map((lang, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--gold)' }}></span>
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{lang}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                {sv ? 'Du lär dig på ett språk du verkligen förstår' : 'You learn in a language you truly understand'}
              </p>
            </div>

            {/* 5. Snabb utveckling */}
            <div className="p-6 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(206,150,8,0.1)', border: '1px solid rgba(206,150,8,0.15)' }}>
                  <TrendingUp size={20} className="text-gold" />
                </div>
                <h4 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? '5. Snabb och tydlig utveckling' : '5. Fast and clear progress'}
                </h4>
              </div>
              <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {sv ? 'Med rätt struktur och kontinuitet ser du snabbt resultat:' : 'With the right structure and continuity you see results quickly:'}
              </p>
              <div className="space-y-1">
                {(sv ? ['Från bokstäver till ord', 'Från osäker till trygg', 'Från långsam till flytande'] : ['From letters to words', 'From insecure to confident', 'From slow to fluent']).map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle size={12} className="text-gold flex-shrink-0" />
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 6. Mer än undervisning */}
            <div className="p-6 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(11,61,46,0.08)', border: '1px solid rgba(11,61,46,0.12)' }}>
                  <Heart size={20} style={{ color: '#0B3D2E' }} />
                </div>
                <h4 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? '6. Mer än bara undervisning' : '6. More than just teaching'}
                </h4>
              </div>
              <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {sv ? 'Detta handlar inte bara om att läsa\u2026 Det handlar om att:' : 'This is not just about reading\u2026 It\'s about:'}
              </p>
              <div className="space-y-1">
                {(sv ? ['Bygga en relation med Koranen', 'Utvecklas som muslim', 'Läsa med förståelse och närvaro'] : ['Build a relationship with the Quran', 'Grow as a Muslim', 'Read with understanding and presence']).map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Sparkles size={12} className="text-gold flex-shrink-0" />
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20" style={{ backgroundColor: 'var(--bg-secondary)' }} data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
              {sv ? 'Vad våra elever och föräldrar säger' : 'What Our Students and Parents Say'}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((item, i) => (
              <div key={i} className="p-6 rounded-2xl" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)' }} data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} size={16} className="text-gold fill-gold" />)}
                </div>
                <p className="text-sm mb-5 leading-relaxed italic" style={{ color: 'var(--text-secondary)' }}>
                  &ldquo;{item.text}&rdquo;
                </p>
                <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
                  &mdash; {item.author}{item.role ? `, ${item.role}` : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" style={{ borderTop: '1px solid var(--border-color)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Rocket size={32} className="text-gold mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'Redo att börja din resa?' : 'Ready to start your journey?'}
          </h2>
          <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
            {sv ? 'Boka en gratis provlektion och upplev skillnaden själv.' : 'Book a free trial lesson and experience the difference yourself.'}
          </p>
          <p className="text-sm mb-8 font-semibold" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'Jag lär dig inte bara att läsa Koranen \u2014 jag lär dig att läsa den rätt.' : 'I don\'t just teach you to read the Quran \u2014 I teach you to read it right.'}
          </p>
          <Link to="/booking" className="btn-gold inline-flex items-center gap-2" data-testid="about-cta">
            {sv ? 'Boka gratis provlektion' : 'Book free trial lesson'}
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
